import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericStepper, { Step } from "@ui-components/Stepper/GenericStepper";
import { SkeletonLine, SkeletonTableRows, SkeletonText } from "@ui-components/Skeleton/GenericSkeleton";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { useAlerts } from "@hooks/useAlerts";
import {
  createTag,
  deleteTag,
  getTagResourceCount,
  listAssignments,
  listUserAssignments,
  replaceAssignments,
  replaceUserAssignments,
  TagDeleteConflictError,
  updateTag,
} from "../requests/tagsStore";
import { productToMount } from "../requests/productAdapters";
import { createCategory } from "../requests/request";
import { usersDataSource } from "../requests/usersDataSource";
import { useCategories, useTags } from "../hooks";
import TagAssignmentsSection from "./TagAssignmentsSection";
import TagUsersSection from "./TagUsersSection";
import type {
  AssignmentDraft,
  ScopeCustomer,
  Tag,
  UserAssignmentDraft,
} from "../types";

/**
 * Tag color palette — sourced from `--color-semantic-bg-tag-*` tokens
 * in `src/theme/styles/themes.css`. Pastel hues (`-200` primitives)
 * in both light and dark themes — works on either background without
 * needing per-theme overrides.
 *
 * Adding a new color = add the token in `themes.css` (both `.theme-light`
 * and `.theme-dark` blocks) and append it here.
 */
const PRESET_COLORS = [
  "var(--color-semantic-bg-tag-cyan)",
  "var(--color-semantic-bg-tag-fuchsia)",
  "var(--color-semantic-bg-tag-turquoise)",
  "var(--color-semantic-bg-tag-blue)",
  "var(--color-semantic-bg-tag-green)",
  "var(--color-semantic-bg-tag-yellow)",
  "var(--color-semantic-bg-tag-red)",
];

/**
 * Tag ↔ user assignments are deferred per `chopper-backend/docs/TAGS-API-CONTRACT.md` §8
 * (RBAC will likely be Tag → Role, not Tag → User). The Users step UI and
 * `TagUsersSection` stay in the codebase; flip to `true` to show Step 3 again.
 *
 * @see `.cursor/skills/tags-manager/SKILL.md` → "Deferred: Wizard Step 3 (Users)"
 */
const TAGS_WIZARD_USERS_STEP_ENABLED = false;

const STEP_LABELS = TAGS_WIZARD_USERS_STEP_ENABLED
  ? (["Basic information", "Resources", "Users"] as const)
  : (["Basic information", "Resources"] as const);

type StepIndex = 0 | 1 | 2;

const TAGS_ROUTE = "/account/tags-manager";

type PendingFormAction = "save" | "delete";

/** Skeleton overlay for the active wizard step while save/delete requests run. */
function TagFormStepSavingOverlay({
  step,
  action,
}: {
  step: StepIndex;
  action: PendingFormAction;
}) {
  const ariaLabel =
    action === "delete" ? "Deleting tag" : "Saving changes";

  if (step === 0) {
    return (
      <div
        className="flex min-h-full w-full flex-col pb-6"
        aria-busy="true"
        aria-label={ariaLabel}
      >
        <div className="mb-5 mt-6 space-y-2">
          <SkeletonLine w={220} h={24} />
          <SkeletonText
            lines={2}
            lineHeight={14}
            widths={["90%", "72%"]}
            gapClassName="space-y-1.5"
          />
        </div>

        <SkeletonLine w={140} h={18} className="mb-6 mt-2" />

        <div className="mb-4 grid max-w-3xl grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
          <SkeletonLine w="100%" h={40} roundedClass="rounded-md" />
          <SkeletonLine w="100%" h={40} roundedClass="rounded-md" />
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4">
          <SkeletonLine w="100%" h={40} roundedClass="rounded-md" />
          <SkeletonLine w="100%" h={40} roundedClass="rounded-md" />
        </div>

        <div className="mb-2">
          <SkeletonLine w={80} h={14} className="mb-3" />
          <SkeletonLine w="100%" h={40} roundedClass="rounded-md" />
        </div>

        <div className="mt-8">
          <SkeletonLine w={48} h={14} className="mb-3" />
          <div className="flex flex-wrap items-center gap-2">
            {Array.from({ length: 8 }).map((_, index) => (
              <SkeletonLine
                key={index}
                w={28}
                h={28}
                roundedClass="rounded-full"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (step === 1) {
    return (
      <div
        className="flex min-h-full w-full flex-col space-y-5 pb-6"
        aria-busy="true"
        aria-label={ariaLabel}
      >
        <div className="space-y-3 rounded-md border border-semantic-border-secondary px-4 py-3">
          <div className="flex justify-end">
            <SkeletonLine w="55%" h={16} />
          </div>
          <div className="flex justify-end gap-2">
            <SkeletonLine w={72} h={20} roundedClass="rounded-sm" />
            <SkeletonLine w={96} h={20} roundedClass="rounded-sm" />
          </div>
          <SkeletonLine w="40%" h={12} className="ml-auto" />
        </div>

        <div className="flex flex-wrap gap-2">
          <SkeletonLine w={112} h={32} roundedClass="rounded-md" />
          <SkeletonLine w={96} h={32} roundedClass="rounded-md" />
          <SkeletonLine w={88} h={32} roundedClass="rounded-md" />
        </div>

        <div className="rounded-md border border-semantic-border-secondary px-3 py-4">
          <SkeletonTableRows
            rows={8}
            cols={1}
            rowHeight={36}
            rowGapClassName="space-y-3"
            animated
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex min-h-full w-full flex-col pb-6"
      aria-busy="true"
      aria-label={ariaLabel}
    >
      <SkeletonTableRows rows={8} cols={2} rowHeight={32} rowGapClassName="space-y-3" animated />
    </div>
  );
}

interface Props {
  /** Determined by the route, not by props in the JSX. Kept for clarity. */
  mode: "create" | "edit";
}

/**
 * Map the wizard's numeric tenant selection to the wire `?tenantId=` value,
 * mirroring `useEffectiveTenantId`'s per-role rules but for an arbitrary
 * selected tenant instead of the global SideNav lens:
 *  - Global Admin → always forward (omitting means backend `findAll`).
 *  - Partner / Customer acting on their OWN tenant → omit (sidesteps the
 *    legacy `getTenantId` string-coercion bug; backend resolves from JWT).
 *  - Otherwise (a child customer) → forward the child id.
 */
function toWireTenantId(
  selected: number,
  partnerId: number,
  tenantType: string
): number | undefined {
  if (!selected) return undefined;
  if (tenantType === "GLOBAL_ADMIN") return selected;
  if (selected === partnerId) return undefined;
  return selected;
}

const TagFormPage: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const { tagId: routeTagId } = useParams<{ tagId: string }>();
  const api = useAxiosAuth0();
  const isEdit = mode === "edit";
  const selectedProduct = useAppSelector((s) => s.product.selectedProduct);
  const mount = productToMount(selectedProduct);
  // Raw SideNav tenant-lens selection. We watch this (not the derived
  // `useEffectiveTenantId`) to detect when the user switches customer or
  // partner in the dropdown while on the tag form — see the kick-out
  // effect below.
  const selectionTenantId = useAppSelector((s) => s.selection.tenantId);
  const selectionPartnerId = useAppSelector((s) => s.selection.partnerId);
  const selectionTenantName = useAppSelector((s) => s.selection.tenantName);
  const tenantType = useAppSelector((s) => s.userProfile.TenantType);
  const partnerName = useAppSelector((s) => s.selection.partnerName);
  const profileTenantId = useAppSelector((s) => s.userProfile.tenantId);
  // Wizard-local tenant the form is pinned to (partner id or a child id).
  // Create: defaults to the SideNav selection, re-pointable via the step-1
  // customer selector. Edit: set to the tag's OWN tenant once it hydrates (see
  // the effect below) so the form operates on the tenant the tag was created
  // under, independent of the SideNav lens.
  const [formTenantId, setFormTenantId] = useState<number>(
    () => selectionTenantId || selectionPartnerId || 0
  );
  // Wire `?tenantId=` for every tag-scoped call. Always derived from the
  // wizard's own tenant selection so categories (§3.7 self) and the resource
  // catalog (§6) scope to `formTenantId` — the tenant picked in create, the
  // tag's own tenant in edit.
  const tenantId = toWireTenantId(formTenantId, selectionPartnerId, tenantType);
  const { tags } = useTags();
  const {
    categories: availableCategories,
    loading: categoriesLoading,
    refresh: refreshCategories,
  } = useCategories(tenantId, true, true);
  const { confirm, alert, Alert } = useAlerts();

  // --- Partner / customer scope selectors (step 1) ----------------------
  // Lets a partner / global admin pin the tag (and therefore the resource
  // catalog + categories) to a specific tenant — without depending on the
  // global SideNav dropdown. Hidden for customer tenants (they only ever see
  // their own resources).
  //   - Global Admin → Partner dropdown + Customer dropdown.
  //   - Partner      → Customer dropdown only.
  //   - Customer     → no dropdown.
  const isGlobalAdmin = tenantType === "GLOBAL_ADMIN";
  const showCustomerSelector = tenantType !== "CUSTOMER";
  // The partner the wizard is currently scoped to. For a partner user this is
  // their own tenant; for a Global Admin it's switchable via the Partner
  // dropdown (defaults to the global selection).
  const [partnerScopeId, setPartnerScopeId] = useState<number>(
    () => selectionPartnerId || 0
  );
  const effectivePartnerId = isGlobalAdmin
    ? partnerScopeId
    : selectionPartnerId || (tenantType === "PARTNER" ? profileTenantId : 0);

  /**
   * Show the "Customer" column in the Step 2 resource picker only when the
   * tag is scoped to the Partner Account itself (no specific customer
   * picked). In that case the catalog returns resources spanning
   * `[partner + children]`, so the owning customer of each row matters.
   * When a specific customer is selected — or the user IS a customer
   * (`showCustomerSelector === false`) — every row belongs to the same
   * known tenant, so the column is redundant and hidden.
   *
   * `formTenantId === effectivePartnerId` is the "Partner Account selected"
   * signal in both modes: create (the customer selector sets it) and edit
   * (it is initialized from the SideNav selection, which also drives the
   * catalog's `?tenantId=`).
   */
  /**
   * True when the wizard is scoped to the **Partner Account itself** (no
   * specific customer picked) and the user can span customers. Drives both
   * the Resources picker "Customer" column and the summary scope label
   * ("Partner assigned:" vs "Customer:").
   */
  const scopeIsPartnerAccount =
    showCustomerSelector && formTenantId === effectivePartnerId;
  const showResourceCustomerColumn = scopeIsPartnerAccount;

  const [partners, setPartners] = useState<ScopeCustomer[]>([]);
  const [customers, setCustomers] = useState<ScopeCustomer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // Global Admin only: load the partner list for the Partner selector.
  useEffect(() => {
    if (!isGlobalAdmin) return;
    let cancelled = false;
    usersDataSource.listPartnersInScope(api).then((list) => {
      if (!cancelled) setPartners(list);
    });
    return () => {
      cancelled = true;
    };
  }, [api, isGlobalAdmin]);

  // Load the customers (direct children) of the partner in scope.
  useEffect(() => {
    if (!showCustomerSelector) return;
    if (!effectivePartnerId) return;
    let cancelled = false;
    setCustomersLoading(true);
    usersDataSource
      .listCustomersInScope(api, effectivePartnerId)
      .then((list) => {
        if (!cancelled) setCustomers(list);
      })
      .finally(() => {
        if (!cancelled) setCustomersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, effectivePartnerId, showCustomerSelector]);

  const partnerOptions = useMemo(
    () => partners.map((p) => ({ id: p.id, label: p.name })),
    [partners]
  );

  const partnerLabelToId = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of partnerOptions) m.set(o.label, o.id);
    return m;
  }, [partnerOptions]);

  const selectedPartnerLabel = useMemo(() => {
    const found = partnerOptions.find((o) => o.id === partnerScopeId);
    return found?.label ?? "";
  }, [partnerOptions, partnerScopeId]);

  // Friendly name of the partner in scope (from the partner list for GA, or
  // the SideNav selection for a partner user) — drives the "Partner Account"
  // option label in the Customer dropdown.
  const scopePartnerName = useMemo(() => {
    if (isGlobalAdmin) {
      return (
        partners.find((p) => p.id === effectivePartnerId)?.name ||
        partnerName ||
        "Partner"
      );
    }
    return partnerName || "Partner";
  }, [isGlobalAdmin, partners, effectivePartnerId, partnerName]);

  // "Partner Account" first (the partner's own tenant), then direct children.
  const customerOptions = useMemo(() => {
    const partnerLabel = `${scopePartnerName} (Partner Account)`;
    const opts: { id: number; label: string }[] = [];
    if (effectivePartnerId) {
      opts.push({ id: effectivePartnerId, label: partnerLabel });
    }
    for (const c of customers) {
      if (c.id === effectivePartnerId) continue;
      opts.push({ id: c.id, label: c.name });
    }
    return opts;
  }, [customers, effectivePartnerId, scopePartnerName]);

  const customerLabelToId = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of customerOptions) m.set(o.label, o.id);
    return m;
  }, [customerOptions]);

  const selectedCustomerLabel = useMemo(() => {
    const found = customerOptions.find((o) => o.id === formTenantId);
    return found?.label ?? "";
  }, [customerOptions, formTenantId]);

  // Human-readable name of the tenant the tag will be assigned to — shown in
  // the step-2 summary. Falls back to the SideNav tenant name for customer
  // users (who have no in-wizard selector).
  const scopeTenantDisplayName =
    selectedCustomerLabel || selectionTenantName || "";
  const tag: Tag | null = useMemo(() => {
    if (!isEdit || !routeTagId) return null;
    return tags.find((t) => t.id === routeTagId) ?? null;
  }, [isEdit, routeTagId, tags]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Category is 1:1 per tag (contract §2.6 / §3.2). The form keeps
  // `categoryId` as the source of truth (what gets sent on save) and
  // `category` (the display name) only for the optional chip render.
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [category, setCategory] = useState("");
  /**
   * New-category name typed by the user — created at Save time, NOT via a
   * separate "Add" action. The live input text IS the staged value; there
   * is no intermediate confirm step (the previous "Add" button was removed
   * to drop the friction of an easy-to-miss extra click).
   *
   * Only meaningful while `creatingCategory` is `true` (the input is only
   * mounted in that mode). Mutual exclusion (single category per tag,
   * contract §2.6) is enforced by the MODE: entering create mode clears any
   * existing selection, and picking an existing category exits create mode
   * and clears this input.
   *
   * Flow:
   *   1. User clicks "New category" → create mode, input mounts.
   *   2. User types a name.
   *   3. On Save → POST §3.8 (idempotent) with the trimmed input → if
   *      success, the returned id becomes the tag's `categoryId`; if
   *      failure, abort the whole save to avoid creating a tag with no
   *      category or leaving orphan data.
   *
   * @see `.cursor/skills/tags-manager/SKILL.md` → "Wizard flow" (deferred-create).
   */
  const [newCategoryInput, setNewCategoryInput] = useState("");
  /**
   * Category UI mode toggle. `false` (default) = pick an existing category
   * from the dropdown; `true` = create a new one via the input. Toggled by
   * the "New category" button and reset whenever an existing category is
   * picked or a new one is confirmed. The three category sources
   * (`categoryId`, `stagedCategoryName`, `newCategoryInput`) are mutually
   * exclusive by construction (see the handlers below).
   */
  const [creatingCategory, setCreatingCategory] = useState(false);
  /**
   * A confirmed-but-not-yet-created new category name. Set when the user
   * clicks **Confirm** in create mode: the input is dismissed and this name
   * is shown in the Category select as the current selection (the §6
   * `GenericSearchableDropdown` renders a `value` that isn't in its items
   * list as raw text — see its `value` effect). The actual POST §3.8 still
   * happens at Save time (deferred-create), so this stays a plain string
   * with no `categoryId` until persisted. Mutually exclusive with
   * `categoryId`.
   */
  const [stagedCategoryName, setStagedCategoryName] = useState<string | null>(
    null
  );
  const [color, setColor] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<AssignmentDraft[]>([]);
  const [originalDrafts, setOriginalDrafts] = useState<AssignmentDraft[]>([]);
  const [userDrafts, setUserDrafts] = useState<UserAssignmentDraft[]>([]);
  const [originalUserDrafts, setOriginalUserDrafts] = useState<
    UserAssignmentDraft[]
  >([]);
  const [hydrated, setHydrated] = useState<boolean>(!isEdit);
  const [pendingAction, setPendingAction] = useState<PendingFormAction | null>(
    null
  );
  const submitting = pendingAction !== null;
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState<StepIndex>(0);

  useEffect(() => {
    if (!TAGS_WIZARD_USERS_STEP_ENABLED && activeStep > 1) {
      setActiveStep(1);
    }
  }, [activeStep]);

  // Edit mode: pin the form to the tag's OWN tenant once it loads, so every
  // tenant-scoped call (categories §3.7 self, resource catalog §6, save §4.2)
  // operates on the tenant the tag was created under — NOT the SideNav lens.
  // Uses `setFormTenantId` directly (NOT `handleSelectFormTenant`) so it does
  // not clear the resources/category being hydrated below.
  useEffect(() => {
    if (!isEdit || !tag) return;
    if (typeof tag.tenantId === "number") {
      setFormTenantId(tag.tenantId);
    }
  }, [isEdit, tag]);

  // Hydrate from store when in edit mode (or when the tag becomes
  // available after the initial async list).
  useEffect(() => {
    if (!isEdit) return;
    if (!tag) return;
    setName(tag.name ?? "");
    setDescription(tag.description ?? "");
    setCategory(tag.category?.name ?? "");
    setCategoryId(tag.category?.id ?? null);
    setColor(tag.color ?? null);
    let cancelled = false;
    Promise.all([
      listAssignments(api, mount, tag.id, tenantId),
      TAGS_WIZARD_USERS_STEP_ENABLED
        ? listUserAssignments(tag.id)
        : Promise.resolve<UserAssignmentDraft[]>([]),
    ]).then(([existingAssignments, existingUsers]) => {
      if (cancelled) return;
      const mappedAssignments = existingAssignments.map<AssignmentDraft>(
        (a) => ({
          serviceType: a.serviceType,
          resourceId: a.resourceId,
          resourceLabel: a.resourceLabel ?? null,
          resourceSubtitle: a.resourceSubtitle ?? null,
        })
      );
      const mappedUsers = existingUsers.map<UserAssignmentDraft>((u) => ({
        auth0Id: u.auth0Id,
        email: u.email,
        displayName: u.displayName,
        customerId: u.customerId,
        customerName: u.customerName,
      }));
      setDrafts(mappedAssignments);
      setOriginalDrafts(mappedAssignments);
      setUserDrafts(mappedUsers);
      setOriginalUserDrafts(mappedUsers);
      setHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isEdit, tag, api, mount, tenantId]);

  // availableCategories comes from GET …/tags/categories (sorted by name, tenant-scoped).
  // Shows all categories regardless of whether they're currently attached to a tag.

  const basicDirty = useMemo(() => {
    if (!tag) return name.trim().length > 0;
    return (
      name.trim() !== (tag.name ?? "") ||
      (description || null) !== (tag.description || null) ||
      categoryId !== (tag.category?.id ?? null) ||
      color !== (tag.color ?? null) ||
      // Cover the uncategorized → new category case: when the tag had
      // `category === null`, clearing the dropdown leaves `categoryId`
      // unchanged (null === null) but a confirmed (`stagedCategoryName`) or
      // still-typed (`newCategoryInput`) new name is a pending change the
      // user expects to save.
      stagedCategoryName !== null ||
      newCategoryInput.trim().length > 0
    );
  }, [
    tag,
    name,
    description,
    categoryId,
    color,
    stagedCategoryName,
    newCategoryInput,
  ]);

  const assignmentsDirty = useMemo(() => {
    if (drafts.length !== originalDrafts.length) return true;
    const key = (d: AssignmentDraft) => `${d.serviceType}::${d.resourceId}`;
    const a = new Set(drafts.map(key));
    const b = new Set(originalDrafts.map(key));
    if (a.size !== b.size) return true;
    return Array.from(a).some((k) => !b.has(k));
  }, [drafts, originalDrafts]);

  const userAssignmentsDirty = useMemo(() => {
    if (userDrafts.length !== originalUserDrafts.length) return true;
    const key = (d: UserAssignmentDraft) =>
      `${d.customerId}::${d.auth0Id}`;
    const a = new Set(userDrafts.map(key));
    const b = new Set(originalUserDrafts.map(key));
    if (a.size !== b.size) return true;
    return Array.from(a).some((k) => !b.has(k));
  }, [userDrafts, originalUserDrafts]);

  const dirty =
    basicDirty ||
    assignmentsDirty ||
    (TAGS_WIZARD_USERS_STEP_ENABLED && userAssignmentsDirty);
  const canProceedFromBasic = name.trim().length > 0;
  /**
   * Delete-tag gate. Per `TAGS-API-CONTRACT.md` §3.5 the backend refuses
   * `DELETE /tags/:id` with 409 while the tag is still attached to any
   * resource — and `replaceAssignments` (the detach-all PUT) is the only
   * way to clear the pivot. Showing a button that we know is going to
   * 409 is hostile UX, so we hide it entirely until the server-side
   * count is zero. We use `originalDrafts` (the snapshot hydrated from
   * §4.1) instead of `drafts` so an in-flight UI removal doesn't surface
   * the button before the user actually persists the detach.
   *
   * Also gated on `hydrated` so the button doesn't flash on screen
   * during the assignments fetch: `originalDrafts` starts as `[]`, and
   * without this guard a tag that actually has N resources would render
   * the button for one frame (initial state) → fetch resolves with N
   * items → button hides. With the guard the button stays hidden until
   * we know the real count.
   */
  const canDeleteTag = isEdit && hydrated && originalDrafts.length === 0;
  /**
   * Step 2 → Step 3 gate: per UX rules, the user must add at least one
   * resource (any service) before moving to Users. Save itself stays
   * lenient — it only requires step 1's name — so a tag can be created
   * with just metadata and resources/users added later via edit.
   */
  const canProceedFromResources = drafts.length > 0;

  const goBack = () => navigate(TAGS_ROUTE);

  /**
   * User picked a different customer / partner in the wizard's own selector.
   * Single-tenant per tag (backend constraint): re-point the form tenant and
   * drop the now cross-tenant selections (resources + the existing category).
   * Deliberately does NOT touch the global SideNav selection — the wizard
   * owns its tenant context locally.
   *
   * If the user already added resources, confirm first since they'll be
   * cleared (they belong to the previous customer). On cancel we keep the
   * current tenant — the selector is controlled by `formTenantId`, so the
   * dropdown reverts on its own.
   */
  const handleSelectFormTenant = async (nextTenantId: number) => {
    if (nextTenantId === formTenantId) return;
    if (drafts.length > 0) {
      const confirmed = await confirm({
        title: "Change customer?",
        description:
          "The resources you've added will be removed because they belong to the current customer. Do you want to continue?",
        confirmLabel: "Yes, change customer",
        cancelLabel: "Cancel",
        variant: "warning",
      });
      if (!confirmed) return;
    }
    setFormTenantId(nextTenantId);
    setDrafts([]);
    setCategoryId(null);
    setCategory("");
  };

  /**
   * Global Admin only: switch the partner the wizard is scoped to. Resets the
   * customer back to that partner's own tenant ("Partner Account") and clears
   * the tenant-scoped selections (with a confirm when resources exist). The
   * Customer dropdown re-populates from the new partner's children.
   */
  const handleSelectPartner = async (nextPartnerId: number) => {
    if (nextPartnerId === partnerScopeId) return;
    if (drafts.length > 0) {
      const confirmed = await confirm({
        title: "Change partner?",
        description:
          "The resources you've added will be removed because they belong to the current customer. Do you want to continue?",
        confirmLabel: "Yes, change partner",
        cancelLabel: "Cancel",
        variant: "warning",
      });
      if (!confirmed) return;
    }
    setPartnerScopeId(nextPartnerId);
    setFormTenantId(nextPartnerId);
    setDrafts([]);
    setCategoryId(null);
    setCategory("");
  };

  const handleSelectCategory = (id: number, name: string) => {
    setCategoryId(id);
    setCategory(name);
    // Picking an existing category always wins over a half-typed / confirmed
    // new one: exit create mode and drop the input + staged name (single
    // category per tag — the three sources can never coexist).
    setCreatingCategory(false);
    setNewCategoryInput("");
    setStagedCategoryName(null);
  };

  const handleClearCategory = () => {
    setCategoryId(null);
    setCategory("");
    setStagedCategoryName(null);
  };

  /**
   * Enter "create new category" mode. Clears any existing selection AND any
   * previously-confirmed new name so the tag's single category slot is
   * owned exclusively by the new input.
   */
  const enterCreateCategory = () => {
    setCategoryId(null);
    setCategory("");
    setStagedCategoryName(null);
    setNewCategoryInput("");
    setCreatingCategory(true);
  };

  /**
   * Confirm the typed new category. Dismisses the input and surfaces the
   * name in the Category select (as `stagedCategoryName`) — it is still
   * only created (POST §3.8) at Save time. No-op on empty input.
   */
  const confirmNewCategory = () => {
    const trimmed = newCategoryInput.trim();
    if (!trimmed) return;
    setStagedCategoryName(trimmed);
    setNewCategoryInput("");
    setCreatingCategory(false);
  };

  /**
   * Leave create mode without confirming — back to the empty selector
   * state. Discards the typed text.
   */
  const cancelCreateCategory = () => {
    setNewCategoryInput("");
    setCreatingCategory(false);
  };

  /** Remove the confirmed (not-yet-created) new category from the select. */
  const dismissStagedCategory = () => {
    setStagedCategoryName(null);
  };

  // Extracts a user-friendly message from an API error. Axios wraps the
  // response in `err.response.data.message`; the default `err.message` is
  // the unhelpful "Request failed with status code N" string.
  function extractSaveErrorMessage(e: unknown): string {
    const apiMessage =
      e != null &&
      typeof e === "object" &&
      "response" in e
        ? (e as { response?: { data?: { message?: string } } }).response?.data
            ?.message
        : undefined;

    if (apiMessage) {
      // Map backend duplicate-name messages to a single friendly phrase so
      // the slug detail ("with slug \"foo\"") never leaks to the UI.
      if (/already exists/i.test(apiMessage)) {
        return "A tag with that name already exists.";
      }
      return apiMessage;
    }

    return e instanceof Error
      ? e.message
      : typeof e === "string"
      ? e
      : "Something went wrong";
  }

  const handleSave = async () => {
    if (!canProceedFromBasic) {
      setError("Name is required");
      setActiveStep(0);
      return;
    }
    setPendingAction("save");
    setError(null);
    // Captured BEFORE the await chain so we can detect the
    // "user just detached every resource in edit mode" case after
    // success — we update `originalDrafts` below and would lose the
    // previous snapshot otherwise.
    const justDetachedAll =
      isEdit && originalDrafts.length > 0 && drafts.length === 0;

    // Pending new category = the confirmed name if the user clicked
    // Confirm, else whatever is still typed in the input (so a user who
    // types and goes straight to Save without confirming still gets it
    // created — deferred-create). Trimmed empties are ignored.
    const pendingNewCategoryName =
      stagedCategoryName || newCategoryInput.trim() || null;

    try {
      // Resolve the effective `categoryId` for the tag payload. The form
      // guarantees mutual exclusion via the category MODE toggle (create
      // mode clears any selection; picking an existing category exits
      // create mode), so at most one source is active here:
      //   - existing dropdown selection → `categoryId` already set.
      //   - typed new name              → POST §3.8 to get its id.
      // POST §3.8 is idempotent (case-insensitive name collision returns
      // the existing row), so re-staging the same name across retries is
      // safe. If the POST fails we abort the whole save so the tag never
      // lands with a null/orphan category — see all-or-nothing below.
      let effectiveCategoryId: number | null = categoryId;
      if (effectiveCategoryId === null && pendingNewCategoryName) {
        const res = await createCategory(
          api,
          mount,
          { name: pendingNewCategoryName },
          tenantId
        );
        if (!res.data.success) {
          throw new Error(
            res.data.message || "Failed to create the new category"
          );
        }
        const created = res.data.data;
        effectiveCategoryId = created.id;
        // Sync local state so the pending name flips to a "real" selection
        // (exit create mode, drop the staged name, show the chip) and the
        // next render reflects the persisted state.
        setNewCategoryInput("");
        setStagedCategoryName(null);
        setCreatingCategory(false);
        setCategoryId(created.id);
        setCategory(created.name);
        void refreshCategories();
      }

      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        categoryId: effectiveCategoryId,
        color,
      };
      const persisted = tag
        ? await updateTag(api, mount, tag.id, payload, tenantId)
        : await createTag(api, mount, payload, tenantId);
      await replaceAssignments(api, mount, persisted.id, drafts, tenantId);
      if (TAGS_WIZARD_USERS_STEP_ENABLED) {
        await replaceUserAssignments(persisted.id, userDrafts);
      }
      // Re-baseline so `assignmentsDirty` / `userAssignmentsDirty` reset
      // and `canDeleteTag` re-evaluates against the now-persisted state.
      setDrafts(drafts);
      setOriginalDrafts(drafts);
      setOriginalUserDrafts(userDrafts);
      if (justDetachedAll) {
        // Stay on the page so the now-visible "Delete tag" button is
        // immediately usable without round-tripping through the list.
        // The §4.2 PUT already updated `state.resourceCountByTagId[id]`
        // to 0, so `handleDelete`'s pre-check (`getTagResourceCount`)
        // will read the fresh cached value.
        await alert({
          title: "Resources detached",
          description:
            "All resources were removed from this tag. You can now delete it.",
          confirmLabel: "OK",
          variant: "success",
        });
        setPendingAction(null);
        return;
      }
      goBack();
    } catch (e) {
      // All-or-nothing: if the category POST or any subsequent call fails,
      // we surface the error and leave the user on the form with their
      // staged input intact so they can retry without re-typing.
      setError(extractSaveErrorMessage(e));
      setPendingAction(null);
    }
  };

  const handleDelete = async () => {
    if (!tag) return;
    setError(null);

    // If there are unsaved resource changes, the visible Step 2 list
    // can disagree with the server-side count. Force the user to save
    // (or discard) first so the delete check uses a consistent state.
    if (assignmentsDirty) {
      await alert({
        title: "Save your changes first",
        description:
          "You have unsaved resource changes. Save (or discard them) before deleting the tag — otherwise the resource count below may be stale.",
        confirmLabel: "OK",
        variant: "warning",
      });
      setActiveStep(1);
      return;
    }

    // Manual flow per TAGS-API-CONTRACT §3.5: pre-check the count.
    // If the tag still has resources, block the delete and keep the
    // user on Step 2 to detach explicitly.
    const count = await getTagResourceCount(api, mount, tag.id, tenantId);
    if (count > 0) {
      await alert({
        title: `Can't delete "${tag.name}" yet`,
        description: `This tag is attached to ${count} resource${
          count === 1 ? "" : "s"
        }. Remove them on the Resources step and save before deleting.`,
        confirmLabel: "Go to Resources",
        variant: "warning",
      });
      setActiveStep(1);
      return;
    }

    const confirmed = await confirm({
      title: `Delete "${tag.name}"?`,
      description: "This action cannot be undone.",
      confirmLabel: "Yes, delete it",
      cancelLabel: "Cancel",
      variant: "warning",
    });
    if (!confirmed) return;

    setPendingAction("delete");
    try {
      await deleteTag(api, mount, tag.id, tenantId);
      goBack();
    } catch (e) {
      if (e instanceof TagDeleteConflictError) {
        await alert({
          title: `Couldn't delete "${tag.name}"`,
          description: `It is now attached to ${e.resourceCount} resource${
            e.resourceCount === 1 ? "" : "s"
          }. Detach them on the Resources step and try again.`,
          confirmLabel: "Go to Resources",
          variant: "warning",
        });
        setActiveStep(1);
      } else {
        setError(e instanceof Error ? e.message : "Failed to delete tag");
      }
      setPendingAction(null);
    }
  };

  // Edit mode + tag list loaded but tag not found → redirect
  useEffect(() => {
    if (!isEdit) return;
    if (tags.length === 0) return;
    if (routeTagId && !tags.find((t) => t.id === routeTagId)) {
      navigate(TAGS_ROUTE, { replace: true });
    }
  }, [isEdit, routeTagId, tags, navigate]);

  /**
   * React to the user switching the SideNav customer / partner dropdown
   * while on the tag form. Tags, categories and resources are all
   * tenant-scoped (TAGS-API-CONTRACT §1) and the backend `findOne` adapter
   * is strict (`WHERE id AND tenantId`), so the data that was valid under
   * the previous tenant is no longer valid under the new one. Behavior
   * splits by mode:
   *
   *   - EDIT  → bail back to the list. The loaded tag belongs to the old
   *     tenant; a Port/Internet/… it points at almost certainly isn't in
   *     the new selection, so `updateTag` / `replaceAssignments` would 404
   *     or touch a mismatched id. A fresh, correctly-scoped list is safer.
   *   - CREATE → stay on the form and RE-SCOPE: keep the tenant-agnostic
   *     metadata the user typed (name / description / color / the typed
   *     NEW category — just a string created on save) but drop everything
   *     tenant-scoped: already-selected resources (`drafts`) and the
   *     selected EXISTING category (`categoryId`). The resource picker
   *     (`TagAssignmentsSection`) and `useCategories` both key off
   *     `useEffectiveTenantId`, so they re-fetch the new tenant's options
   *     on their own — we only clear the stale selections + notify.
   *
   * Baseline is captured once the form is READY (`hydrated`) — NOT on the
   * "first truthy tenant". In partner view `selection.tenantId` is
   * legitimately `0` (the dropdown shows "Partner view" via the
   * `effectivePartnerId` fallback while `selection.tenantId` stays unset),
   * so anchoring on "first non-zero tenant" mistook the user's FIRST
   * customer pick for the baseline — the off-by-one where the first switch
   * didn't react and only the second did. By the time `hydrated` flips true
   * the boot hydration (`0 → real tenant`) has already settled, so every
   * selection change observed afterwards is user-driven. In create mode the
   * baseline is re-anchored after each handled switch so a later switch
   * back to the original tenant is still detected.
   */
  const baselineSelectionRef = useRef<{
    tenantId: number;
    partnerId: number;
  } | null>(null);
  useEffect(() => {
    if (!hydrated) return;
    if (baselineSelectionRef.current === null) {
      baselineSelectionRef.current = {
        tenantId: selectionTenantId,
        partnerId: selectionPartnerId,
      };
      return;
    }
    const baseline = baselineSelectionRef.current;
    const changed =
      baseline.tenantId !== selectionTenantId ||
      baseline.partnerId !== selectionPartnerId;
    if (!changed) return;

    if (isEdit) {
      // Edit mode operates on the tag's OWN tenant (`formTenantId` is pinned to
      // `tag.tenantId`), independent of the SideNav lens — so a global dropdown
      // change is irrelevant here. Just re-anchor the baseline and stay on the
      // form (no kick-out, no reset).
      baselineSelectionRef.current = {
        tenantId: selectionTenantId,
        partnerId: selectionPartnerId,
      };
      return;
    }

    // Create mode — the global dropdown moved while in the wizard. Per the
    // agreed rule, the wizard FOLLOWS the global: re-point the form tenant to
    // the new global selection and reset the tenant-scoped selections. (The
    // reverse never happens — changing the wizard selector does NOT touch the
    // global; see handleSelectFormTenant.)
    baselineSelectionRef.current = {
      tenantId: selectionTenantId,
      partnerId: selectionPartnerId,
    };
    setPartnerScopeId(selectionPartnerId || 0);
    setFormTenantId(selectionTenantId || selectionPartnerId || 0);
    const hadResources = drafts.length > 0;
    const hadExistingCategory = categoryId !== null;
    if (hadResources) setDrafts([]);
    if (hadExistingCategory) {
      setCategoryId(null);
      setCategory("");
    }
    if (hadResources || hadExistingCategory) {
      void alert({
        title: "Customer changed",
        description:
          "Your selected resources and category were reset to match the newly selected customer. The tag name, description and color you entered were kept.",
        confirmLabel: "OK",
        variant: "success",
      });
    }
  }, [
    hydrated,
    isEdit,
    selectionTenantId,
    selectionPartnerId,
    navigate,
    drafts,
    categoryId,
    alert,
  ]);

  const stepperSteps: Step[] = useMemo(() => {
    return STEP_LABELS.map((title, idx): Step => {
      if (idx === activeStep) return { title, status: "active" };
      // Step 2 (Resources) requires step 1's name.
      if (idx === 1 && !canProceedFromBasic) {
        return { title, status: "disabled" };
      }
      // Step 3 (Users) requires step 1's name AND at least one resource.
      if (
        TAGS_WIZARD_USERS_STEP_ENABLED &&
        idx === 2 &&
        (!canProceedFromBasic || !canProceedFromResources)
      ) {
        return { title, status: "disabled" };
      }
      if (idx < activeStep) return { title, status: "completed" };
      return { title };
    });
  }, [activeStep, canProceedFromBasic, canProceedFromResources]);

  const handleStepClick = (idx: number) => {
    if (idx === 1 && !canProceedFromBasic) return;
    if (
      TAGS_WIZARD_USERS_STEP_ENABLED &&
      idx === 2 &&
      (!canProceedFromBasic || !canProceedFromResources)
    ) {
      return;
    }
    if (idx >= 0 && idx < STEP_LABELS.length) {
      setActiveStep(idx as StepIndex);
    }
  };

  if (isEdit && !hydrated) {
    return (
      <div
        className="p-6 flex flex-col gap-4 h-full overflow-hidden"
        aria-busy="true"
        aria-label="Loading tag"
      >
        {/* Mirror the mounted wizard's sizing (`flex-1 min-h-0` card inside an
            `h-full` column) so the card fills the same height and the skeleton
            doesn't visibly resize when the real form takes over. */}
        <GenericCard
          isCollapsible={false}
          className="flex-1 min-h-0 flex flex-col"
          bodyContent={
            <div className="flex flex-col items-center gap-8 py-6 h-full min-h-0">
              <div className="flex gap-6 w-full max-w-md justify-center">
                <SkeletonLine w={120} h={14} />
                <SkeletonLine w={120} h={14} />
              </div>
              <div className="w-full max-w-3xl space-y-6 px-4">
                <SkeletonLine w={160} h={20} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-md" />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-md" />
                <div className="flex gap-4">
                  <SkeletonLine w="50%" h={40} roundedClass="rounded-md" />
                  <SkeletonLine w="50%" h={40} roundedClass="rounded-md" />
                </div>
                <SkeletonText lines={2} lineHeight={14} />
              </div>
            </div>
          }
        />
      </div>
    );
  }

  // ----- Step 1 content: Basic Information -----
  const basicStepContent = (
    <div>
      <div className="mb-5 mt-6">
        <h2 className="text-xl font-semibold text-semantic-text-primary">
          {isEdit ? (
            <>
              Edit: {" "}
              <span className="text-semantic-text-secondary">
                {tag?.name ?? "tag"}
              </span>
            </>
          ) : (
            "Create tag"
          )}
        </h2>
        <p className="text-sm text-semantic-text-secondary mt-1 w-full">
          Tags are shared across products. Configure the basic details
          and attach resources from any product the tag should cover.
        </p>
      </div>

      <h3 className="text-md font-semibold mb-6 mt-8 text-semantic-text-primary">
        Basic information
      </h3>

      {/*
       * Customer scope selector — the wizard's OWN tenant context, decoupled
       * from the global SideNav dropdown. Picking a customer (or the Partner
       * Account) scopes the resource catalog AND the categories below to that
       * single tenant, and the tag is created under it. Single-tenant per tag
       * for now (backend constraint); changing it clears the current
       * resources + category (with a confirm when resources exist). Locked in
       * edit mode — a tag can't be re-tenanted yet.
       */}
      {showCustomerSelector && (
        <div className="mb-4 md:max-w-3xl">
          <div className="grid grid-cols-1 gap-3 md:gap-4 md:grid-cols-2">
            {isGlobalAdmin && (
              <GenericDropdown
                fixedPosition
                dropdownMaxHeightClass="max-h-48"
                label="Partner"
                items={partnerOptions.map((o) => o.label)}
                value={selectedPartnerLabel}
                defaultLabel="Select a partner"
                emptyItemsMessage="No partners available"
                onSelect={(pickedLabel) => {
                  const nextId = partnerLabelToId.get(pickedLabel);
                  if (nextId) void handleSelectPartner(nextId);
                }}
                disabled={isEdit || partnerOptions.length === 0}
                widthClass="w-full"
                minWidthClass="min-w-0"
                maxWidthClass="max-w-full"
                size="md"
                triggerHeightClass="!py-2"
              />
            )}
            <GenericDropdown
              fixedPosition
              dropdownMaxHeightClass="max-h-48"
              label="Customer"
              items={customerOptions.map((o) => o.label)}
              value={selectedCustomerLabel}
              defaultLabel={
                customersLoading ? "Loading customers…" : "Select a customer"
              }
              emptyItemsMessage="No customers in scope"
              onSelect={(pickedLabel) => {
                const nextId = customerLabelToId.get(pickedLabel);
                if (nextId) void handleSelectFormTenant(nextId);
              }}
              disabled={isEdit || customerOptions.length === 0}
              widthClass="w-full"
              minWidthClass="min-w-0"
              maxWidthClass="max-w-full"
              size="md"
              triggerHeightClass="!py-2"
            />
          </div>
          <p className="text-xs text-semantic-text-secondary mt-1">
            {isEdit
              ? "Resources and categories are scoped to the tag's customer."
              : "Resources and categories are scoped to the selected tenant. Changing it resets your selection."}
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <GenericInput
          label="Name"
          value={name}
          onChange={setName}
          placeholder="e.g. production"
          required
          hasError={!!error && !name.trim()}
        />
        <GenericInput
          label="Description"
          value={description}
          onChange={setDescription}
          placeholder="Optional short description"
        />
      </div>

      {/* Category section — two mutually-exclusive modes (single category
       * per tag, contract §2.6):
       *   - SELECT (default): pick an existing category from the dropdown.
       *     A "New category" button beside it switches to CREATE mode.
       *   - CREATE: an input (below the dropdown) takes a new name, with
       *     "Cancel" + "Confirm" beside the dropdown. Confirm dismisses the
       *     input and shows the typed name IN the select (as a pending
       *     selection); Cancel discards and returns to SELECT mode.
       *
       * The modes can never both claim the tag's single slot:
       *   - Entering CREATE clears any existing selection / staged name.
       *   - Picking a dropdown option exits CREATE and clears input + staged
       *     (the dropdown stays interactive in CREATE mode precisely so this
       *     switch-back works).
       *
       * There is NO "Add" button: the pending name (`stagedCategoryName`
       * once confirmed, or the raw input otherwise) is only created via
       * POST §3.8 in `handleSave` (deferred-create).
       *
       * Layout: the dropdown sits in the first column of a 2-col grid so it
       * matches the Name input's width above; the action buttons live in the
       * second column. The chip / create-input render under the dropdown,
       * constrained to the same column width. */}
      <div className="mt-4 mb-4">
        <div className="grid grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-semantic-text-secondary mb-2">
              Category
            </label>
            <GenericSearchableDropdown
              items={availableCategories.map((c) => ({
                value: String(c.id),
                label: c.name,
              }))}
              // Third state beyond "existing id" / "empty": a confirmed new
              // name. The dropdown renders a `value` not present in its items
              // list as raw text, so the staged name shows as the selection.
              value={
                categoryId !== null
                  ? String(categoryId)
                  : stagedCategoryName !== null
                  ? stagedCategoryName
                  : ""
              }
              onSelect={(idStr) => {
                if (!idStr) {
                  handleClearCategory();
                  return;
                }
                const id = Number.parseInt(idStr, 10);
                const found = availableCategories.find((c) => c.id === id);
                if (found) handleSelectCategory(found.id, found.name);
              }}
              defaultLabel={
                categoriesLoading
                  ? "Loading categories…"
                  : "Select an existing category"
              }
              allowCustomValues={false}
              emptyItemsMessage="No categories yet — create one →"
              // Spinner inside the control while categories (re)load after a
              // tenant switch.
              loading={categoriesLoading}
              // Only disabled while (re)loading. It stays interactive in
              // CREATE mode on purpose: picking an option there switches
              // back to SELECT mode and discards the typed name.
              disabled={categoriesLoading}
              // The wizard card has its own `overflow-y-auto` body — without
              // `fixedPosition` the menu gets clipped by that scroll
              // container. `dropdownMaxHeightClass` caps the list height so
              // long category catalogs stay scrollable inside the popup.
              fixedPosition
              dropdownMaxHeightClass="max-h-48"
              size="input"
              widthClass="w-full"
              minWidthClass="min-w-0"
              maxWidthClass="max-w-full"
            />
          </div>
          <div className="flex items-center gap-2">
            {creatingCategory ? (
              <>
                <GenericButton
                  label="Cancel"
                  variant="secondary"
                  size="md"
                  onClick={cancelCreateCategory}
                />
                <GenericButton
                  label="Confirm"
                  variant="brand"
                  size="md"
                  onClick={confirmNewCategory}
                  disabled={!newCategoryInput.trim()}
                />
              </>
            ) : (
              <GenericButton
                label="New category"
                variant="secondary"
                size="md"
                onClick={enterCreateCategory}
              />
            )}
          </div>
        </div>

        {/* SELECT mode: chip for the current category (existing OR a
         * confirmed-but-not-yet-created new one). The × clears it. */}
        {!creatingCategory && (category || stagedCategoryName) ? (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium bg-semantic-bg-brand-secondary text-semantic-text-brand border border-semantic-border-brand-secondary"
                title={
                  stagedCategoryName && !category
                    ? "Will be created on save"
                    : undefined
                }
              >
                {category || stagedCategoryName}
                <button
                  type="button"
                  onClick={
                    category ? handleClearCategory : dismissStagedCategory
                  }
                  className="text-semantic-text-brand hover:text-semantic-text-primary"
                  aria-label="Clear selected category"
                  title="Clear category"
                >
                  ×
                </button>
              </span>
            </div>
          </div>
        ) : null}

        {/* CREATE mode: new-category input + deferred-create hint, aligned
         * under the dropdown. Confirm surfaces it in the select; the actual
         * POST §3.8 still fires on Save. */}
        {creatingCategory && (
          <div className="grid grid-cols-2 gap-4">
            <div className="mt-2">
              <GenericInput
                label=""
                value={newCategoryInput}
                onChange={setNewCategoryInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    confirmNewCategory();
                  }
                }}
                placeholder="Create a new category"
              />
              <span className="block text-xs text-semantic-text-secondary mt-1">
                {newCategoryInput.trim()
                  ? "Click Confirm to set it — it's created when you save the tag."
                  : "Type a name, then Confirm. It's created when you save the tag."}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 mb-8">
        <label className="block text-sm font-medium text-semantic-text-secondary mb-2">
          Color
        </label>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setColor(null)}
            className={`w-7 h-7 rounded-full border-2 ${
              color === null
                ? "border-semantic-border-brand-secondary"
                : "border-semantic-border-secondary"
            } bg-semantic-bg-primary flex items-center justify-center text-xs text-semantic-text-secondary`}
            aria-label="No color"
          >
            —
          </button>
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              style={{ background: c }}
              className={`w-7 h-7 rounded-full border-2 ${
                color === c
                  ? "border-semantic-border-brand-secondary"
                  : "border-transparent"
              }`}
              aria-label={`Color ${c}`}
            />
          ))}
        </div>
      </div>
    </div>
  );

  // ----- Step 2 content: Resources -----
  // A small read-only summary on top so the user keeps context of what
  // they configured in step 1 without leaving the page.
  // Resolved tag color for the summary pill; falls back to a neutral when no
  // color is chosen. Tints the pill via color-mix so any PRESET color reads
  // correctly in both light and dark themes.
  const summaryTagColor = color ?? "var(--color-primitive-gray-300)";

  const resourcesStepContent = (
    <div className="space-y-5 mb-10">
      <div className="px-4 py-3 rounded-md border border-semantic-border-secondary">
        {/* Left-aligned summary recap (Step 1 context): the tag name lives in
         * a pill tinted with the tag's own color, with category + scope shown
         * as labelled metadata and the description underneath. */}
        <div className="flex items-center justify-between gap-x-6 gap-y-3 flex-wrap min-w-0">
          <span
            className="inline-flex items-center gap-2 min-w-0 max-w-full rounded-full border pl-2.5 pr-3 py-1"
            style={{
              backgroundColor: `color-mix(in srgb, ${summaryTagColor} 16%, transparent)`,
              borderColor: `color-mix(in srgb, ${summaryTagColor} 48%, transparent)`,
            }}
          >
            <span
              aria-hidden
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: summaryTagColor }}
            />
            <span className="text-sm font-semibold text-semantic-text-primary truncate">
              {name || (
                <em className="italic font-normal text-semantic-text-secondary">
                  (unnamed)
                </em>
              )}
            </span>
          </span>

          <div className="flex items-center gap-5 flex-wrap">
            {(category || stagedCategoryName || newCategoryInput.trim()) && (
              <div className="flex flex-col items-start gap-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-semantic-text-secondary leading-none">
                  Category
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium bg-semantic-bg-brand-secondary text-semantic-text-brand border border-semantic-border-brand-secondary">
                  {category || stagedCategoryName || newCategoryInput.trim()}
                </span>
              </div>
            )}
            {(scopeIsPartnerAccount
              ? scopePartnerName
              : scopeTenantDisplayName) && (
              <div className="flex flex-col items-start gap-1 min-w-0">
                <span className="text-[10px] font-bold uppercase tracking-wide text-semantic-text-secondary leading-none">
                  {scopeIsPartnerAccount ? "Partner" : "Customer"}
                </span>
                <span className="text-sm font-semibold text-semantic-text-primary truncate max-w-[28ch]">
                  {scopeIsPartnerAccount
                    ? scopePartnerName
                    : scopeTenantDisplayName}
                </span>
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-semantic-text-secondary truncate mt-3">
          {description || "No description"}
        </p>
      </div>

      <TagAssignmentsSection
        drafts={drafts}
        onChange={setDrafts}
        excludeTaggedByTagId={
          isEdit && tag && /^\d+$/.test(tag.id)
            ? Number.parseInt(tag.id, 10)
            : undefined
        }
        selectedTenantId={formTenantId}
        wireTenantId={tenantId}
        showCustomerColumn={showResourceCustomerColumn}
      />
    </div>
  );

  // ----- Step 3 content: Users -----
  // Same compact summary as step 2 so the user keeps context, then the
  // users section. Sharing the summary block keeps visual rhythm and
  // makes "Edit basic info" available from any non-basic step.
  const usersStepContent = (
    <div className="space-y-5">
      <div className="px-4 py-3 rounded-md border border-semantic-border-secondary bg-semantic-bg-secondary">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <span
              aria-hidden
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{
                background: color ?? "var(--color-primitive-gray-300)",
              }}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-semantic-text-primary truncate">
                  {name || <em className="italic">(unnamed)</em>}
                </span>
                {category && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium bg-semantic-bg-secondary text-semantic-text-primary">
                    {category}
                  </span>
                )}
                <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-medium bg-semantic-bg-brand-secondary text-semantic-text-brand">
                  {drafts.length} resource{drafts.length === 1 ? "" : "s"}
                </span>
              </div>
              <span className="block text-xs text-semantic-text-secondary truncate mt-0.5">
                {description || "No description"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <GenericButton
              label="Edit basic info"
              variant="secondary"
              size="sm"
              onClick={() => setActiveStep(0)}
            />
            <GenericButton
              label="Edit resources"
              variant="secondary"
              size="sm"
              onClick={() => setActiveStep(1)}
            />
          </div>
        </div>
      </div>

      <TagUsersSection drafts={userDrafts} onChange={setUserDrafts} />
    </div>
  );

  const stepContent = TAGS_WIZARD_USERS_STEP_ENABLED
    ? activeStep === 0
      ? basicStepContent
      : activeStep === 1
        ? resourcesStepContent
        : usersStepContent
    : activeStep === 0
      ? basicStepContent
      : resourcesStepContent;

  return (
    <div className="p-6 flex flex-col gap-4 h-full overflow-hidden">
      {error && (
        <div className="flex-shrink-0 px-3 py-2 rounded-md text-sm bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <GenericCard
        isCollapsible={false}
        className="flex-1 min-h-0 flex flex-col"
        bodyContent={
          <div className="flex flex-col h-full min-h-0">
            {/* Action buttons — top-right corner, inside the card. The bottom
             * border mirrors the footer nav's top divider. */}
            <div className="flex items-center justify-end gap-2 flex-wrap px-5 py-3 border-b border-semantic-border-secondary flex-shrink-0">
              {canDeleteTag && (
                <GenericButton
                  label="Delete tag"
                  variant="danger"
                  size="md"
                  onClick={handleDelete}
                  disabled={submitting}
                />
              )}
              <GenericButton
                label="Cancel"
                variant="secondary"
                size="md"
                onClick={goBack}
                disabled={submitting}
              />
              {/*
               * Primary commit action, always top-right next to Cancel:
               *   edit   → Save changes (a dirty, valid form)
               *   create → Create tag (needs a name AND ≥1 resource; stays
               *            disabled on step 1 until resources are attached)
               */}
              {isEdit ? (
                <GenericButton
                  label="Save changes"
                  variant="brand"
                  size="md"
                  loading={submitting}
                  onClick={handleSave}
                  disabled={submitting || !canProceedFromBasic || !dirty}
                />
              ) : (
                <GenericButton
                  label="Create tag"
                  variant="brand"
                  size="md"
                  loading={submitting}
                  onClick={handleSave}
                  disabled={
                    submitting ||
                    !canProceedFromBasic ||
                    !canProceedFromResources
                  }
                  aria-label="Create tag and return to list"
                />
              )}
            </div>
            {/* Stepper — half width, centered, slim height */}
            <div className="px-5 pt-4 pb-3 flex-shrink-0">
              <div className="w-full md:w-1/2 mx-auto">
                <GenericStepper
                  steps={stepperSteps}
                  onStepClick={handleStepClick}
                  size="sm"
                  activeClassName="bg-semantic-bg-primary shadow-sm text-semantic-text-primary font-medium"
                  completedClassName="text-gray-400"
                  inactiveClassName="text-gray-400"
                  disabledClassName="text-gray-400 opacity-70"
                />
              </div>
            </div>

            {/* Step content — scrollable region */}
            <div className="flex min-h-0 flex-1 flex-col">
              {pendingAction ? (
                <div
                  className="min-h-0 flex-1 overflow-y-auto custom-scrollbar bg-semantic-bg-primary px-5 pb-5"
                  aria-busy="true"
                >
                  <TagFormStepSavingOverlay
                    step={activeStep}
                    action={pendingAction}
                  />
                </div>
              ) : (
                <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar px-5 pb-5">
                  {stepContent}
                </div>
              )}
            </div>

            {/* Bottom step nav */}
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-semantic-border-secondary flex-shrink-0">
              <span className="text-xs text-semantic-text-secondary">
                Step {activeStep + 1} of {STEP_LABELS.length} ·{" "}
                {STEP_LABELS[activeStep]}
              </span>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                {TAGS_WIZARD_USERS_STEP_ENABLED && activeStep === 1 && (
                  <GenericButton
                    label="Next: users →"
                    variant="brand"
                    size="md"
                    onClick={() => setActiveStep(2 as StepIndex)}
                    disabled={!canProceedFromResources}
                  />
                )}
                {activeStep === 0 && (
                  <GenericButton
                    label="Next: resources →"
                    variant="brand"
                    size="md"
                    onClick={() => setActiveStep(1)}
                    disabled={!canProceedFromBasic}
                  />
                )}
                {/* Edit mode owns Save in the header, so the footer on the
                 * Resources step just needs a way back to step 1 — mirrors the
                 * "Next: resources →" affordance shown in step 1. */}
                {activeStep === 1 && isEdit && (
                  <GenericButton
                    label="← Back: basic information"
                    variant="secondary"
                    size="md"
                    onClick={() => setActiveStep(0)}
                    disabled={submitting}
                  />
                )}
                {/*
                 * Create mode: the commit action ("Create tag") now lives in
                 * the header next to Cancel, so the footer on the Resources
                 * step only needs a way back to step 1 (the stepper is the
                 * other way back).
                 */}
                {!TAGS_WIZARD_USERS_STEP_ENABLED &&
                  activeStep === 1 &&
                  !isEdit && (
                  <GenericButton
                    label="← Back to basic info"
                    variant="secondary"
                    size="md"
                    onClick={() => setActiveStep(0)}
                    disabled={submitting}
                  />
                )}
                {TAGS_WIZARD_USERS_STEP_ENABLED &&
                  activeStep === 2 &&
                  userDrafts.length > 0 &&
                  !isEdit && (
                  <div className="flex flex-col items-end gap-1.5 pl-2 border-l border-semantic-border-secondary">
                    <GenericButton
                      label="Create tag"
                      variant="brand"
                      size="md"
                      loading={submitting}
                      onClick={handleSave}
                      disabled={submitting || !canProceedFromBasic}
                      aria-label="Create tag and return to list"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        }
      />
      {Alert}
    </div>
  );
};

export default TagFormPage;
